import { z } from "zod";

/**
 * App settings (0058): global key/value settings, `app_settings`. The keys
 * are a closed list (`ck_app_settings_key`, generated from `APP_SETTING_KEYS`),
 * so adding one is a migration, on purpose. Values travel as strings, as in
 * WSL's `app_settings`, and each key validates its own.
 */

export const TSR_DESIGN_THEMES = ["default", "wsl"] as const;
export type TsrDesignTheme = (typeof TSR_DESIGN_THEMES)[number];

/** Ported from WSL (`src/routes/app_settings/app_settings.routes.ts`). */
export const TSR_SETTING_KEYS = ["tsr_design_theme", "tsr_sidebar_show_all_sports", "tsr_sidebar_show_para_meets", "tsr_sidebar_presentation_only"] as const;
export type TsrSettingKey = (typeof TSR_SETTING_KEYS)[number];

export const APP_SETTING_KEYS = [...TSR_SETTING_KEYS] as const;
export type AppSettingKey = (typeof APP_SETTING_KEYS)[number];

const boolText = z.enum(["true", "false"]);

/** The validator of each key's value. */
export const TSR_SETTING_VALUES = {
	tsr_design_theme: z.enum(TSR_DESIGN_THEMES),
	tsr_sidebar_show_all_sports: boolText,
	tsr_sidebar_show_para_meets: boolText,
	tsr_sidebar_presentation_only: boolText,
} as const satisfies Record<TsrSettingKey, z.ZodType<string>>;

/** What a key reads as before anyone has written it. */
export const TSR_SETTING_DEFAULTS = {
	tsr_design_theme: "default",
	tsr_sidebar_show_all_sports: "false",
	tsr_sidebar_show_para_meets: "false",
	tsr_sidebar_presentation_only: "false",
} as const satisfies Record<TsrSettingKey, string>;

/**
 * `tsr_sidebar_presentation_only`: the sports a presentation shows, by SEA
 * Games `sports.code` — Weightlifting, Sepak Takraw, Swimming (Aquatics),
 * Athletics, Badminton, Tenpin Bowling, Boxing. Overrides `tsr_sidebar_show_all_sports`.
 */
export const TSR_PRESENTATION_SPORT_CODES = ["WLF", "STK", "AQ", "AT", "BD", "BOW", "BOX"] as const;

export const isTsrSettingKey = (key: string): key is TsrSettingKey => (TSR_SETTING_KEYS as readonly string[]).includes(key);

/** `GET /app-settings/tsr-public` and `/app-settings/tsr`: every key, defaults filled in. */
export type TsrSettings = Record<TsrSettingKey, string>;

/** `PUT /app-settings/tsr/:key` answers the stored pair. */
export interface AppSettingWritten {
	key: AppSettingKey;
	value: string;
	updated_at: string;
}

export const putAppSettingBody = z.object({ value: z.string().trim().min(1).max(200) });

/**
 * The games whose sports are PARA sports (`games.code`, apps/api games seed).
 * A sport code can name a sport in both games (APG may well reuse AQ / AT), so
 * TSR matches a para module only to a sport of these games, and a SEA Games
 * module only to a sport of the others — see apps/tsr `scoreboard.registry.ts`.
 */
export const TSR_PARA_GAMES_CODE = "APG";

/** Whether a sport (by its `games_code`) is a para sport. Unknown games: not para. */
export const tsrIsParaGames = (gamesCode: string | null | undefined): boolean => gamesCode?.toUpperCase() === TSR_PARA_GAMES_CODE;

/**
 * PROVISIONAL `sports.code`s of the ASEAN Para Games sports TSR runs. The APG
 * has no sports seeded yet (2026-10-27), so these are TSR's guesses, kept in
 * ONE place: when the games data lands, correct them here. Every para module
 * also matches by name (its `sportName` / aliases), so a different code
 * still resolves while this list catches up.
 */
export const TSR_PARA_SPORT_CODES = {
	archery: "PAR",
	athletics: "PAT",
	boccia: "BOC",
	chess: "PCH",
	powerlifting: "PPL",
	swimming: "PSW",
	tableTennis: "PTT",
	tenpinBowling: "PBW",
} as const;
